import { useCallback, useEffect, useState } from "react";
import { ETAPA, ORDEM_ETAPAS } from "../utils/toc/etapas";
import { ZONAS_PULMAO_DEFAULT } from "../utils/toc/bufferManager";
import { PCT_MATERIAIS_DEFAULT } from "../utils/toc/throughputCalculator";
import { getItemTenant, setItemTenant } from "../utils/tenantStorage";

const STORAGE_KEY = "pharuerp:kpis-ppcp:parametros";

const OE_DEFAULT = Object.freeze({
  folha: 0,
  encargos: 0,
  aluguel: 0,
  energia: 0,
  depreciacao: 0,
  administrativas: 0,
  comerciais: 0,
  outras: 0,
});

export const PARAMETROS_TOC_DEFAULT = Object.freeze({
  pctMateriaisMedio: PCT_MATERIAIS_DEFAULT,
  metaAmbientesMes: 0,
  metaGanhoMesRS: 0,
  oeMensal: { ...OE_DEFAULT },
  zonasPulmao: { ...ZONAS_PULMAO_DEFAULT },
  ccrEtapa: null,
  modoVisualizacao: "simples",
  // I-077: flag de calibracao explicito. Quando false, o detector (que usa a
  // capacidade fixa de 4 amb/dia em todas as etapas) NAO deve ser usado para
  // tomada de decisao real. A capacidade medida da fabrica fica em
  // /legado/capacidade.
  calibracaoCompleta: false,
  // I-079: janela do detector parametrizavel.
  janelaDiasUteis: 7,
});

function clampNumero(value, { min = 0, max = Number.POSITIVE_INFINITY } = {}) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.min(max, Math.max(min, parsed));
}

export function normalizarParametros(input) {
  const base = input && typeof input === "object" ? input : {};
  // `capacidadePorEtapa` de versoes antigas e descartada de proposito: a
  // tela que a editava saiu (o card mostra a etapa com maior CARGA) e um
  // valor invisivel nao pode continuar decidindo a etapa.
  const oe = base.oeMensal || {};
  const zonas = base.zonasPulmao || {};

  const oeMensal = {};
  for (const chave of Object.keys(OE_DEFAULT)) {
    oeMensal[chave] = clampNumero(oe[chave] ?? 0, { min: 0 });
  }

  const verde = clampNumero(zonas.verde ?? ZONAS_PULMAO_DEFAULT.verde, {
    min: 0,
    max: 1,
  });
  const amareloRaw = clampNumero(zonas.amarelo ?? ZONAS_PULMAO_DEFAULT.amarelo, {
    min: 0,
    max: 1,
  });
  const amarelo = Math.max(verde, amareloRaw);
  const vermelhoRaw = clampNumero(
    zonas.vermelho ?? ZONAS_PULMAO_DEFAULT.vermelho,
    { min: 0, max: 1 }
  );
  const vermelho = Math.max(amarelo, vermelhoRaw);

  const modo = base.modoVisualizacao === "avancado" ? "avancado" : "simples";
  const ccr =
    base.ccrEtapa && ORDEM_ETAPAS.includes(base.ccrEtapa)
      ? base.ccrEtapa
      : null;

  return {
    pctMateriaisMedio: clampNumero(
      base.pctMateriaisMedio ?? PCT_MATERIAIS_DEFAULT,
      { min: 0, max: 1 }
    ),
    metaAmbientesMes: clampNumero(base.metaAmbientesMes ?? 0, { min: 0 }),
    metaGanhoMesRS: clampNumero(base.metaGanhoMesRS ?? 0, { min: 0 }),
    oeMensal,
    zonasPulmao: { verde, amarelo, vermelho },
    ccrEtapa: ccr,
    modoVisualizacao: modo,
    calibracaoCompleta: Boolean(base.calibracaoCompleta),
    janelaDiasUteis: clampNumero(base.janelaDiasUteis ?? 7, { min: 1, max: 90 }),
  };
}

function lerStorage() {
  if (typeof window === "undefined") return PARAMETROS_TOC_DEFAULT;
  try {
    const raw = getItemTenant(STORAGE_KEY);
    if (!raw) return { ...PARAMETROS_TOC_DEFAULT };
    return normalizarParametros(JSON.parse(raw));
  } catch {
    return { ...PARAMETROS_TOC_DEFAULT };
  }
}

function escreverStorage(parametros) {
  if (typeof window === "undefined") return;
  try {
    setItemTenant(STORAGE_KEY, JSON.stringify(parametros));
  } catch {
    // localStorage indisponível (modo privado, quota cheia) — silencioso.
  }
}

export function useParametrosTOC() {
  const [parametros, setParametros] = useState(lerStorage);

  useEffect(() => {
    escreverStorage(parametros);
  }, [parametros]);

  const atualizar = useCallback((patch) => {
    setParametros((prev) => {
      const merged =
        typeof patch === "function"
          ? patch(prev)
          : { ...prev, ...patch };
      return normalizarParametros(merged);
    });
  }, []);

  const atualizarOE = useCallback((chave, valor) => {
    if (!(chave in OE_DEFAULT)) return;
    setParametros((prev) =>
      normalizarParametros({
        ...prev,
        oeMensal: { ...prev.oeMensal, [chave]: valor },
      })
    );
  }, []);

  const atualizarZonas = useCallback((patch) => {
    setParametros((prev) =>
      normalizarParametros({
        ...prev,
        zonasPulmao: { ...prev.zonasPulmao, ...patch },
      })
    );
  }, []);

  const definirModo = useCallback((modo) => {
    setParametros((prev) =>
      normalizarParametros({ ...prev, modoVisualizacao: modo })
    );
  }, []);

  const definirCcr = useCallback((etapa) => {
    setParametros((prev) =>
      normalizarParametros({ ...prev, ccrEtapa: etapa })
    );
  }, []);

  const resetar = useCallback(() => {
    setParametros({ ...PARAMETROS_TOC_DEFAULT });
  }, []);

  return {
    parametros,
    atualizar,
    atualizarOE,
    atualizarZonas,
    definirModo,
    definirCcr,
    resetar,
  };
}

export const __test__ = { lerStorage, escreverStorage, STORAGE_KEY, ETAPA };
